import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import './Cart.css';

/* ══════════════════════════════════════════════════════
   GRAPHICS — pure inline SVG line-art
   (no icon fonts, no emoji, no external images)
   ══════════════════════════════════════════════════════ */
const base = (size, sw) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: sw,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

const IcArrowLeft = ({ size = 17, sw = 1.8 }) => (
  <svg {...base(size, sw)}><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></svg>
);

const IcArrowRight = ({ size = 17, sw = 1.8 }) => (
  <svg {...base(size, sw)}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
);

const IcPlus = ({ size = 16, sw = 1.8 }) => (
  <svg {...base(size, sw)}><path d="M12 5v14M5 12h14" /></svg>
);

const IcMinus = ({ size = 16, sw = 1.8 }) => (
  <svg {...base(size, sw)}><path d="M5 12h14" /></svg>
);

const IcTrash = ({ size = 16, sw = 1.6 }) => (
  <svg {...base(size, sw)}>
    <path d="M4 7h16" />
    <path d="M9.5 7V5.2A1.2 1.2 0 0 1 10.7 4h2.6a1.2 1.2 0 0 1 1.2 1.2V7" />
    <path d="M18 7l-.85 12.1A2 2 0 0 1 15.16 21H8.84A2 2 0 0 1 6.85 19.1L6 7" />
    <path d="M10.2 11.4v5M13.8 11.4v5" />
  </svg>
);

const IcTruck = ({ size = 17, sw = 1.6 }) => (
  <svg {...base(size, sw)}>
    <path d="M2.5 7.2A1.7 1.7 0 0 1 4.2 5.5H14v10.2H2.5Z" />
    <path d="M14 9h3.3a2 2 0 0 1 1.68.92l2.02 3.08v2.7H14" />
    <circle cx="7" cy="18" r="1.8" />
    <circle cx="17.4" cy="18" r="1.8" />
  </svg>
);

const IcReturn = ({ size = 17, sw = 1.6 }) => (
  <svg {...base(size, sw)}>
    <path d="M3.2 12a8.8 8.8 0 1 0 2.85-6.5" />
    <path d="M3 4.6v4.6h4.6" />
  </svg>
);

const IcShield = ({ size = 17, sw = 1.6 }) => (
  <svg {...base(size, sw)}>
    <path d="M12 3.3 19 6v5.7c0 4.4-2.9 7.5-7 9-4.1-1.5-7-4.6-7-9V6Z" />
    <path d="m9.2 12.1 2 2 3.6-3.9" />
  </svg>
);

const IcLock = ({ size = 15, sw = 1.7 }) => (
  <svg {...base(size, sw)}>
    <rect x="4.6" y="10.4" width="14.8" height="9.8" rx="2.6" />
    <path d="M8.2 10.4V7.9a3.8 3.8 0 0 1 7.6 0v2.5" />
  </svg>
);

const IcCheck = ({ size = 16, sw = 1.9 }) => (
  <svg {...base(size, sw)}><path d="m5 12.6 4.5 4.4L19 7" /></svg>
);

const IcStar = ({ size = 15, sw = 1.6 }) => (
  <svg {...base(size, sw)}>
    <path d="m12 4 2.45 5.03 5.55.8-4.02 3.9.95 5.52L12 16.64 7.07 19.25l.95-5.52L4 9.83l5.55-.8Z" />
  </svg>
);

const IcHeadset = ({ size = 15, sw = 1.6 }) => (
  <svg {...base(size, sw)}>
    <path d="M4.2 13.4v-1.2a7.8 7.8 0 0 1 15.6 0v1.2" />
    <rect x="2.4" y="13" width="4" height="6.2" rx="1.7" />
    <rect x="17.6" y="13" width="4" height="6.2" rx="1.7" />
    <path d="M19.6 19.2v.6a2.4 2.4 0 0 1-2.4 2.4h-3.6" />
  </svg>
);

const IcWallet = ({ size = 17, sw = 1.6 }) => (
  <svg {...base(size, sw)}>
    <path d="M3 7.4A2.4 2.4 0 0 1 5.4 5h11a2 2 0 0 1 2 2v1.2" />
    <rect x="3" y="7.4" width="18" height="11.6" rx="2.6" />
    <circle cx="16.4" cy="13.2" r="1.25" />
  </svg>
);

const IcSpark = ({ size = 15, sw = 1.6 }) => (
  <svg {...base(size, sw)}>
    <path d="M12 3.5 13.6 8 18 9.6 13.6 11.2 12 15.7 10.4 11.2 6 9.6 10.4 8Z" />
    <path d="M18.2 15.4l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7Z" />
  </svg>
);

const IcBox = ({ size = 30, sw = 1.4 }) => (
  <svg {...base(size, sw)}>
    <path d="M12 3 20 7.5v9L12 21 4 16.5v-9Z" />
    <path d="M4 7.5 12 12l8-4.5M12 12v9" />
  </svg>
);

/* Large empty-cart illustration */
const CartArt = () => (
  <svg width="150" height="150" viewBox="0 0 150 150" fill="none" aria-hidden="true">
    <circle cx="75" cy="75" r="75" fill="#F3ECE3" />
    <circle cx="75" cy="75" r="55" fill="#FBF7F2" />
    <path
      d="M42 50h10l8.5 39h42L112 62H56"
      stroke="#C2703B" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"
    />
    <circle cx="64" cy="101" r="5.4" stroke="#17140F" strokeWidth="3.2" />
    <circle cx="95" cy="101" r="5.4" stroke="#17140F" strokeWidth="3.2" />
    <path d="M46 34v10M41 39h10" stroke="#C2703B" strokeWidth="3.2" strokeLinecap="round" opacity=".55" />
    <path d="M112 40v7M108.5 43.5h7" stroke="#17140F" strokeWidth="2.6" strokeLinecap="round" opacity=".22" />
  </svg>
);

/* ══════════════════════════════════════════════════════
   SAFE DATA HELPERS
   ══════════════════════════════════════════════════════ */
const catName = (cat) => {
  if (!cat) return '';
  if (typeof cat === 'string') return cat;
  if (typeof cat === 'object') return cat.name || cat.title || '';
  return '';
};

const imgUrl = (item, backend = 'http://127.0.0.1:8000') => {
  if (!item) return null;
  const raw = item.image || item.image_url || item.thumbnail;
  if (!raw || typeof raw !== 'string') return null;
  return raw.startsWith('http') ? raw : `${backend}${raw}`;
};

const deliveryOf = (item) => {
  if (!item) return 0;
  const raw =
    item.delivery_charge ??
    item.deliveryCharge ??
    item.shipping_charge ??
    item.shippingCharge ??
    0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/* ══════════════════════════════════════════════════════
   STATIC DATA
   ══════════════════════════════════════════════════════ */
const perks = [
  { icon: <IcTruck size={17} />, title: 'Fast delivery', desc: 'Dispatched within 24h' },
  { icon: <IcReturn size={17} />, title: '7-day returns', desc: 'No questions asked' },
  { icon: <IcShield size={17} />, title: 'Secure payment', desc: '256-bit encrypted' },
  { icon: <IcCheck size={17} />, title: 'Genuine goods', desc: 'Certified quality' },
];

const policies = [
  {
    icon: <IcReturn size={21} />,
    title: '7-Day Return',
    text: 'Return any item within 7 days of delivery — no questions asked.',
  },
  {
    icon: <IcWallet size={21} />,
    title: 'Easy Refund',
    text: 'Refunds are credited to your bank account or UPI within 3–5 business days.',
  },
  {
    icon: <IcTruck size={21} />,
    title: 'Fast Delivery',
    text: 'Orders are dispatched within 24 hours and delivered quickly across India.',
  },
  {
    icon: <IcShield size={21} />,
    title: 'Safe Payment',
    text: 'UPI, cards, and net banking — all secure and encrypted. Your money is safe.',
  },
];

const trustItems = [
  { icon: <IcLock size={14} />, label: '100% Secure' },
  { icon: <IcStar size={14} />, label: '4.8 Rating' },
  { icon: <IcHeadset size={14} />, label: '24/7 Support' },
  { icon: <IcShield size={14} />, label: 'Certified Brand' },
];

/* ══════════════════════════════════════════════════════
   COMPONENT
   ══════════════════════════════════════════════════════ */
export default function Cart() {
  const { cart, removeFromCart, updateQuantity, totalPrice } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
    } else {
      navigate('/checkout');
    }
  };

  /* ---------- Calculations ---------- */
  const subtotal = totalPrice || 0;
  const delivery = cart.reduce((sum, item) => sum + deliveryOf(item), 0);
  const tax = Math.round(subtotal * 0.05);
  const grandTotal = subtotal + delivery + tax;

  const savings = cart.reduce((sum, item) => {
    const old = item.old_price || item.price;
    return sum + (old - item.price) * item.quantity;
  }, 0);

  const totalItems = cart.reduce((s, i) => s + i.quantity, 0);

  const formatPrice = (v) =>
    new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v);

  return (
    <>
      <Navbar />

      <div className="ct-page">
        <div className="ct-shell">

          {/* ══════════ HEADER ══════════ */}
          <header className="ct-head">
            <Link to="/shop" className="ct-back">
              <IcArrowLeft />
              Continue shopping
            </Link>

            <div className="ct-head-row">
              <h1 className="ct-title">
                Your Cart
                {cart.length > 0 && (
                  <span className="ct-count">
                    {totalItems} {totalItems === 1 ? 'item' : 'items'}
                  </span>
                )}
              </h1>
              <p className="ct-sub">
                {cart.length > 0
                  ? 'Review your picks and head to checkout.'
                  : 'Nothing here yet — your next favourite is waiting.'}
              </p>
            </div>
          </header>

          {cart.length === 0 ? (
            /* ══════════ EMPTY STATE ══════════ */
            <section className="ct-empty">
              <CartArt />
              <h2>Your cart is empty</h2>
              <p>Explore the collection and add something you love.</p>
              <Link to="/shop" className="ct-cta ct-cta-inline">
                Start shopping <IcArrowRight />
              </Link>
            </section>
          ) : (
            /* ══════════ MAIN LAYOUT ══════════ */
            <div className="ct-layout">

              {/* ---------- LEFT ---------- */}
              <div className="ct-main">

                <div className="ct-ship is-done">
                  <div className="ct-ship-row">
                    <span className="ct-ship-label">
                      <IcTruck size={16} /> Fast delivery — every order dispatched within 24 hours
                    </span>
                    <span className="ct-ship-pct">
                      <IcCheck size={14} /> Priority
                    </span>
                  </div>
                </div>

                {/* Items */}
                <div className="ct-list">
                  {cart.map((item) => {
                    const imageUrl = imgUrl(item);
                    const name = catName(item.category);
                    const href = `/product/${item.slug || item.id}`;

                    const lineTotal = item.price * item.quantity;
                    const lineOld = item.old_price ? item.old_price * item.quantity : null;
                    const lineDelivery = deliveryOf(item);

                    return (
                      <article className="ct-item" key={item.id}>
                        <Link to={href} className="ct-thumb">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={item.name || 'product'}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : (
                            <IcBox />
                          )}
                          {item.stock === 0 && <span className="ct-oos">Out of stock</span>}
                        </Link>

                        <div className="ct-item-body">
                          <div className="ct-item-top">
                            <div className="ct-item-txt">
                              <Link to={href} className="ct-item-name">
                                {item.name}
                              </Link>

                              <div className="ct-item-meta">
                                {name && <span>{name}</span>}
                                {name && <span className="ct-dot" />}
                                <span>₹{formatPrice(item.price)} each</span>
                                <span className="ct-dot" />
                                <span className="ct-delivery">
                                  <IcTruck size={13} />
                                  {lineDelivery > 0
                                    ? `Delivery ₹${formatPrice(lineDelivery)}`
                                    : 'Delivery free'}
                                </span>
                              </div>
                            </div>

                            <button
                              className="ct-remove"
                              onClick={() => removeFromCart(item.id)}
                              aria-label={`Remove ${item.name}`}
                            >
                              <IcTrash />
                            </button>
                          </div>

                          <div className="ct-item-bot">
                            <div className="ct-qty">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                aria-label="Decrease quantity"
                              >
                                <IcMinus size={15} />
                              </button>
                              <span>{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                aria-label="Increase quantity"
                              >
                                <IcPlus size={15} />
                              </button>
                            </div>

                            <div className="ct-item-price">
                              {lineOld && lineOld > lineTotal && (
                                <span className="ct-old">₹{formatPrice(lineOld)}</span>
                              )}
                              <span className="ct-now">₹{formatPrice(lineTotal)}</span>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>

                {/* Perks strip */}
                <div className="ct-perks">
                  {perks.map((p) => (
                    <div className="ct-perk" key={p.title}>
                      <span className="ct-perk-ico">{p.icon}</span>
                      <span className="ct-perk-txt">
                        <span className="ct-perk-t">{p.title}</span>
                        <span className="ct-perk-d">{p.desc}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ---------- RIGHT : SUMMARY ---------- */}
              <aside className="ct-side">
                <div className="ct-card">
                  <h3 className="ct-card-title">Order Summary</h3>

                  {/* Breakdown */}
                  <div className="ct-lines">
                    <div className="ct-line">
                      <span>Subtotal · {totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
                      <b>₹{formatPrice(subtotal)}</b>
                    </div>

                    {savings > 0 && (
                      <div className="ct-line is-save">
                        <span>You save</span>
                        <b>− ₹{formatPrice(savings)}</b>
                      </div>
                    )}

                    <div className="ct-line">
                      <span>Delivery charges</span>
                      {delivery > 0 ? (
                        <b>₹{formatPrice(delivery)}</b>
                      ) : (
                        <b className="ct-free">FREE</b>
                      )}
                    </div>

                    <div className="ct-line">
                      <span>Tax (5%)</span>
                      <b>₹{formatPrice(tax)}</b>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="ct-total">
                    <span>Total</span>
                    <span className="ct-total-amt">₹{formatPrice(grandTotal)}</span>
                  </div>

                  {savings > 0 && (
                    <div className="ct-save">
                      <IcSpark />
                      You're saving ₹{formatPrice(savings)} on this order
                    </div>
                  )}

                  <button className="ct-cta" onClick={handleCheckout}>
                    Proceed to checkout
                    <IcArrowRight />
                  </button>

                  {!user && (
                    <p className="ct-hint">
                      <IcLock size={13} />
                      Login required to place your order
                    </p>
                  )}

                  <div className="ct-secure">
                    <span><IcLock size={12} /> Secure</span>
                    <span className="ct-dot" />
                    <span>SSL encrypted</span>
                    <span className="ct-dot" />
                    <span>UPI · Cards</span>
                  </div>
                </div>
              </aside>
            </div>
          )}

          {/* ══════════ POLICIES ══════════ */}
          <section className="ct-sec">
            <div className="ct-sec-head">
              <div>
                <h2>Why shop with us</h2>
                <p>Trusted by thousands of happy customers across India</p>
              </div>
            </div>

            <div className="ct-policies">
              {policies.map((p) => (
                <div className="ct-pol" key={p.title}>
                  <span className="ct-pol-ico">{p.icon}</span>
                  <h3>{p.title}</h3>
                  <p>{p.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ══════════ TRUST STRIP ══════════ */}
          <div className="ct-strip">
            {trustItems.map((t) => (
              <span className="ct-strip-item" key={t.label}>
                {t.icon}
                {t.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}